<script setup lang="ts">
import { brandRef } from "../brand-state";
/**
 * Two read-only pages from one set of data (2026-09-27 review):
 *
 *   - **Approvals** (`view="approvals"`, tab id `governance`) — where every
 *     assistant stands on its three steps, the review round, and who signs.
 *     The only statuses here are decisions a person makes.
 *   - **Evidence** (`view="evidence"`) — which safeguards are in force, for an
 *     auditor or the ISO 27001 review: platform posture, per-assistant
 *     controls, downloads. Nothing on it is a task; a deliberate setting (PII
 *     in monitor mode) reads as "partly", never as something "to fix".
 *
 * The original report (ADR 0050 §4) — read-only.
 *
 * The question it answers is the one an AI Board, a DPO or an auditor asks
 * first: *which AI systems do we run, who owns each, what risk class is it,
 * when was it last reviewed, is each safeguard on, and are the agreements
 * for its data signed?* Top to bottom:
 *   - **Open items** — the compliance part of the admin's to-do list
 *     (`utils/todo.ts`), so this page and Overview can never disagree.
 *   - **Platform posture** — deployment-wide controls as status chips.
 *   - **Inventory** — one row per assistant. A row opens that assistant's
 *     Compliance section in the assistant editor.
 *   - **Downloads** — the evidence export and the review pack.
 *
 * It used to be an editor too: a drawer here classified, approved and
 * recorded sign-offs for an assistant that the Assistants tab edited in a
 * second drawer. Editing moved into the assistant editor (2026-09-24
 * review), so each decision has exactly one place where it is made. This
 * page is the record.
 *
 * What it deliberately does not claim: it **evidences** that controls are
 * configured and that they fired; it does not certify compliance with the
 * EU AI Act, ISO/IEC 42001 or GDPR. Framework references say which clause
 * a control speaks to, not that the clause is satisfied.
 */
import { computed, onMounted, ref } from "vue";
import {
  AdminError,
  type ControlStatusRow,
  type ControlStatus,
  type GovernanceAssistant,
  type DataSource,
  type GovernanceResponse,
  getDataSources,
  getGovernance,
  getGovernanceReport,
} from "../api/admin";
import { useToast } from "../composables/useToast";
import { saveBlob } from "../utils/download";
import {
  STATUS_LABELS,
  ACCESS_MODEL_LABELS,
  accessModelText,
  accessModelTone,
  accessTone,
  fmtTs,
  frameworkTags,
  riskTone,
  today,
} from "../utils/governance";
import { adminLink } from "../utils/vocabulary";
import ResponsibilitiesCard from "./ResponsibilitiesCard.vue";
import ReviewRound from "./ReviewRound.vue";
import SkeletonRows from "./SkeletonRows.vue";
import StatusChip from "./StatusChip.vue";

const props = withDefaults(
  defineProps<{ devUser: string; view?: "approvals" | "evidence" }>(),
  { view: "approvals" },
);
const toast = useToast();

const loading = ref(false);
const data = ref<GovernanceResponse | null>(null);
const sources = ref<DataSource[]>([]);

async function reload(): Promise<void> {
  loading.value = true;
  void getDataSources(props.devUser)
    .then((r) => (sources.value = r.sources))
    .catch(() => (sources.value = []));
  try {
    data.value = await getGovernance(props.devUser);
  } catch (e) {
    toast.error(
      "Could not load the compliance report",
      e instanceof AdminError ? e.message : String(e),
    );
  } finally {
    loading.value = false;
  }
}

// Approvals reads its own, lighter endpoint (ReviewRound); only Evidence
// needs the ledger-scanning report.
onMounted(() => {
  if (props.view === "evidence") void reload();
});

// ── Catalogue lookups ─────────────────────────────────────────────────────

const controlsById = computed(
  () => new Map((data.value?.controls ?? []).map((c) => [c.id, c])),
);
function controlTitle(id: string): string {
  return controlsById.value.get(id)?.title ?? id;
}
/** Two or three words, for chips. The full title is on the hover card. */
function controlShort(id: string): string {
  const c = controlsById.value.get(id);
  return c?.short || c?.title || id;
}
function tagsFor(id: string): string[] {
  return frameworkTags(controlsById.value.get(id));
}

/** The deployment-wide settings, labelled, in words. */
const postureFacts = computed(() => {
  const d = data.value?.deployment;
  if (!d) return [];
  const pii = !d.pii_redact_enabled
    ? "Off"
    : d.pii_enforce === false
      ? `Detecting, not masking (${d.pii_mode})`
      : `Detecting and masking (${d.pii_mode})`;
  return [
    { label: "Personal data", value: pii },
    {
      label: "Spend limits",
      value: !d.budget_enabled
        ? "Off"
        : d.budget_enforced
          ? "Enforced"
          : "Measured only",
    },
    {
      label: "Chats deleted after",
      value: `${d.inactivity_delete_days} days inactive`,
    },
    {
      label: "Audit log",
      value: d.audit_retention_days ? `${d.audit_retention_days} days` : "Kept",
    },
    {
      label: "Prompts stored in traces",
      value: d.telemetry.capture_content ? "Yes" : "No",
    },
  ];
});

/** Hover card for a posture chip. */
const hoveredControl = ref<string | null>(null);

// ── Control tally: a bar plus the one number that prompts action ─────────

function tally(rows: ControlStatusRow[]): Record<ControlStatus, number> {
  const t: Record<ControlStatus, number> = { ok: 0, warn: 0, gap: 0, na: 0 };
  for (const r of rows) t[r.status] += 1;
  return t;
}
/** In words, without "to fix": a partly-in-force control is often a
 * deliberate setting, and the decisions live on the Approvals page. */
function tallyWords(rows: ControlStatusRow[]): string {
  const t = tally(rows);
  return [
    `${t.ok} in force`,
    t.warn ? `${t.warn} partly` : "",
    t.gap ? `${t.gap} not in force` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}
function tallySegments(
  rows: ControlStatusRow[],
): { tone: string; n: number }[] {
  const t = tally(rows);
  return [
    { tone: "ok", n: t.ok },
    { tone: "warn", n: t.warn },
    { tone: "gap", n: t.gap },
  ].filter((seg) => seg.n > 0);
}
function tallyLabel(rows: ControlStatusRow[]): string {
  const t = tally(rows);
  return `${t.ok} ok, ${t.warn} warn, ${t.gap} gap`;
}

function open(a: GovernanceAssistant): void {
  window.location.hash = adminLink("assistants", {
    assistant: a.slug,
    section: "compliance",
  });
}

// ── Downloads ─────────────────────────────────────────────────────────────

const days = ref<30 | 90 | 365>(30);
const downloading = ref<"" | "json" | "md">("");

async function download(format: "json" | "md"): Promise<void> {
  if (downloading.value) return;
  downloading.value = format;
  try {
    const blob = await getGovernanceReport(format, days.value, props.devUser);
    saveBlob(blob, `choir-governance-report-${today}.${format}`);
  } catch (e) {
    toast.error(
      format === "json"
        ? "Could not download evidence export"
        : "Could not download the evidence report",
      e instanceof AdminError ? e.message : String(e),
    );
  } finally {
    downloading.value = "";
  }
}
</script>

<template>
  <div class="admin-governance">
    <header class="admin-governance__header">
      <div class="admin-governance__title">
        <h2>{{ view === "evidence" ? "Evidence" : "Approvals" }}</h2>
        <span v-if="view === 'approvals'" class="muted small">
          Where every assistant stands, and who signs.
        </span>
        <span v-else-if="data" class="muted small">
          Which safeguards are in force — for auditors and the ISO 27001
          review. Read-only; nothing here is a task. Generated
          {{ fmtTs(data.generated_at) }}.
        </span>
      </div>
      <div v-if="view === 'evidence'" class="admin-governance__actions">
        <label class="days">
          <span class="muted small">Report window</span>
          <select v-model.number="days">
            <option :value="30">30 days</option>
            <option :value="90">90 days</option>
            <option :value="365">365 days</option>
          </select>
        </label>
        <button
          class="btn"
          :disabled="downloading !== ''"
          @click="download('json')"
        >
          {{
            downloading === "json" ? "Preparing…" : "Download evidence (JSON)"
          }}
        </button>
        <button
          class="btn"
          :disabled="downloading !== ''"
          @click="download('md')"
        >
          {{
            downloading === "md"
              ? "Preparing…"
              : "Download evidence report (Markdown)"
          }}
        </button>
        <button class="btn btn--ghost" :disabled="loading" @click="reload">
          Refresh
        </button>
      </div>
    </header>

    <template v-if="view === 'approvals'">
        <!-- ── Where every assistant stands (ADR 0066) ─────────────────── -->
        <ReviewRound :dev-user="devUser" />
        <!-- ── Who signs, owns and decides ─────────────────────────────── -->
        <ResponsibilitiesCard :dev-user="devUser" />
    </template>

    <SkeletonRows v-else-if="loading && !data" :rows="6" />

    <template v-else-if="data">
      <!-- ── Platform posture ───────────────────────────────────────── -->
      <section class="card posture">
        <div class="card__head">
          <h3>Platform posture</h3>
          <dl class="posture__facts">
            <div v-for="f in postureFacts" :key="f.label">
              <dt>{{ f.label }}</dt>
              <dd>{{ f.value }}</dd>
            </div>
          </dl>
        </div>
        <div class="posture__strip">
          <span
            v-for="pc in data.platform_controls"
            :key="pc.id"
            class="posture__chip-wrap"
            tabindex="0"
            :aria-label="`${controlTitle(pc.id)}: ${STATUS_LABELS[pc.status]}. ${pc.detail}`"
            @mouseenter="hoveredControl = pc.id"
            @mouseleave="hoveredControl = null"
            @focus="hoveredControl = pc.id"
            @blur="hoveredControl = null"
          >
            <span :class="['chip', `chip--${pc.status}`]">
              <span class="chip__dot" aria-hidden="true"></span>
              {{ controlShort(pc.id) }}
            </span>
            <span
              v-if="hoveredControl === pc.id"
              class="hover-card"
              role="tooltip"
            >
              <strong>{{ controlTitle(pc.id) }}</strong>
              <span v-if="controlsById.get(pc.id)?.summary" class="muted">
                {{ controlsById.get(pc.id)?.summary }}
              </span>
              <span>{{ pc.detail }}</span>
              <span v-if="tagsFor(pc.id).length" class="fw-tags">
                <code v-for="t in tagsFor(pc.id)" :key="t" class="fw">{{
                  t
                }}</code>
              </span>
            </span>
          </span>
          <span v-if="data.platform_controls.length === 0" class="muted">
            No platform controls in the catalogue.
          </span>
        </div>
      </section>

      <!-- ── How each source decides who sees what (ADR 0067) ────────── -->
      <section v-if="sources.length" class="card">
        <div class="card__head">
          <h3>Data sources: who sees what</h3>
          <span class="muted small">
            The target: each source {{ ACCESS_MODEL_LABELS.record }}, or the
            {{ ACCESS_MODEL_LABELS.system }} to its own users, or open.
            “Planned” means a {{ brandRef.productName }} group decides meanwhile, confirmed by
            the data owner.
          </span>
        </div>
        <div class="table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Access</th>
                <th>How</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="src in sources" :key="src.key">
                <td>
                  <div class="name">{{ src.label }}</div>
                  <div class="muted small">{{ src.system }}</div>
                </td>
                <td>
                  <StatusChip :tone="accessModelTone(src)" dot>{{
                    accessModelText(src)
                  }}</StatusChip>
                </td>
                <td class="small">{{ src.access_how || "—" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ── Inventory ──────────────────────────────────────────────── -->
      <section class="card">
        <div class="card__head">
          <h3>Inventory</h3>
          <span class="muted small"
            >Click an assistant for its safeguards, in detail.</span
          >
        </div>
        <div class="table-wrap">
          <table class="admin-table inventory">
            <thead>
              <tr>
                <th>Assistant</th>
                <th>Owner</th>
                <th>Risk class</th>
                <th>Access class</th>
                <th>Safeguards</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="a in data.assistants"
                :key="a.slug"
                class="inventory__row"
                :class="{ 'row--disabled': !a.enabled }"
                tabindex="0"
                @click="open(a)"
                @keydown.enter.prevent="open(a)"
                @keydown.space.prevent="open(a)"
              >
                <td>
                  <div class="name">{{ a.name || a.slug }}</div>
                  <div class="muted mono small">
                    {{ a.slug
                    }}<template v-if="!a.enabled"> · disabled</template>
                  </div>
                </td>
                <td>
                  <span v-if="a.owner_email">{{ a.owner_email }}</span>
                  <span v-else-if="a.owner_oid" class="mono small">{{
                    a.owner_oid
                  }}</span>
                  <span v-else class="gap-text">no owner</span>
                  <div v-if="a.co_owner_emails?.length" class="muted small">
                    + {{ a.co_owner_emails.join(", ") }}
                  </div>
                </td>
                <td>
                  <StatusChip :tone="riskTone(a.governance.risk_class)">
                    {{ a.governance.risk_class }}
                  </StatusChip>
                </td>
                <td>
                  <StatusChip
                    v-if="a.access_class"
                    :tone="accessTone(a.access_class.key)"
                    :title="a.access_class.detail"
                  >
                    {{ a.access_class.label }}
                  </StatusChip>
                  <span v-else class="muted">—</span>
                </td>
                <td class="inventory__tally">
                  <span
                    class="ctrl-bar"
                    role="img"
                    :aria-label="tallyLabel(a.controls)"
                    :title="tallyLabel(a.controls)"
                  >
                    <i
                      v-for="seg in tallySegments(a.controls)"
                      :key="seg.tone"
                      :class="`ctrl-bar__seg ctrl-bar__seg--${seg.tone}`"
                      :style="{ flexGrow: seg.n }"
                    />
                  </span>
                  <span class="small muted">{{ tallyWords(a.controls) }}</span>
                </td>
              </tr>
              <tr v-if="data.assistants.length === 0">
                <td colspan="5" class="muted">
                  No assistants yet. Create one on the Assistants tab and it
                  appears here with every field as a gap.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<style scoped>
.admin-governance {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-governance__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: var(--space-3);
  flex-wrap: wrap;
}
.admin-governance__title {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.admin-governance__title h2 {
  margin: 0;
}
.admin-governance__actions {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.days {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.card__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--space-3);
  flex-wrap: wrap;
}
.card h3 {
  margin: 0;
  font-size: 1rem;
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.8rem;
}
.mono {
  font-family: var(--font-mono, monospace);
}
.gap-text {
  color: var(--color-error);
  font-weight: 600;
}
/* ── Status chips ─────────────────────────────────────────────────────
   One vocabulary for platform posture, per-assistant controls, risk class
   and flags. The amber is the same literal the budget bars use — there is
   no warning token in theme.css. */
.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
  color: var(--color-muted);
}
.chip__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: currentColor;
  flex: none;
}
.chip--ok {
  background: color-mix(in srgb, var(--color-success) 14%, transparent);
  color: var(--color-success);
}
/* ── Platform posture strip ─────────────────────────────────────────── */
.posture__strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.posture__chip-wrap {
  position: relative;
  display: inline-block;
  cursor: help;
}
.posture__chip-wrap:focus {
  outline: none;
}
.posture__chip-wrap:focus-visible .chip {
  outline: 2px solid var(--color-accent);
  outline-offset: 1px;
}
.hover-card {
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  z-index: 60;
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: max-content;
  max-width: min(22rem, 80vw);
  padding: 0.6rem 0.75rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.14);
  font-size: 0.8rem;
  line-height: 1.4;
  color: var(--color-ink);
  white-space: normal;
  text-align: left;
}
/* ── Framework reference tags ─────────────────────────────────────────── */
.fw-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.fw {
  font-family: var(--font-mono, monospace);
  font-size: 0.68rem;
  padding: 0.05rem 0.35rem;
  border-radius: 3px;
  background: color-mix(in srgb, var(--color-ink) 7%, transparent);
  color: var(--color-muted);
  white-space: nowrap;
}
/* ── Inventory table ──────────────────────────────────────────────────── */
.table-wrap {
  overflow-x: auto;
  max-width: 100%;
  /* A flex item defaults to `min-width: auto`, which means it refuses to
     shrink below its content — so the wrapper grew to the table's full
     width and `overflow-x` never engaged. The evidence table (611px) was
     dragging the whole drawer sideways at phone width instead of scrolling
     inside its own box. */
  min-width: 0;
}
.inventory__row {
  cursor: pointer;
}
.inventory__row:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--color-accent);
}
.row--disabled {
  opacity: 0.6;
}
.inventory__flag {
  width: 1.5rem;
  padding-right: 0 !important;
}
.flag {
  display: inline-grid;
  place-items: center;
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 800;
  background: color-mix(in srgb, var(--color-warn) 18%, transparent);
  color: var(--color-warn-ink);
}
.name {
  font-weight: 600;
}
/* ── Needs attention ─────────────────────────────────────────────────── */
.attention__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.attention__head h3 {
  margin: 0;
}
.attention__list {
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: grid;
  gap: 0.3rem;
}
.attention__list li {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}
.attention__list strong {
  font-variant-numeric: tabular-nums;
  min-width: 1.6rem;
  text-align: right;
}
.attention__group h4 {
  margin: 0.6rem 0 0;
  font-size: 0.85rem;
}
.attention__targets {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0 0.6rem;
  margin-left: 0.4rem;
  font-size: 0.8rem;
}
.attention__targets a {
  color: var(--color-accent);
  text-decoration: none;
}
.attention__note {
  margin: 0.75rem 0 0;
}
/* Labelled deployment facts, replacing a run-on string of identifiers. */
.posture__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1.5rem;
  margin: 0;
}
.posture__facts > div {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
}
.posture__facts dt {
  font-size: 0.76rem;
  color: var(--color-muted);
}
.posture__facts dd {
  margin: 0;
  font-size: 0.82rem;
}
.inventory__tally {
  white-space: nowrap;
}
.ctrl-bar {
  display: flex;
  width: 4.5rem;
  height: 0.45rem;
  gap: 1px;
  border-radius: var(--radius-pill);
  overflow: hidden;
  background: var(--color-border);
}
.tally {
  display: inline-block;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  margin-right: 0.35rem;
}
.tally + .tally::before {
  content: "· ";
  color: var(--color-muted);
}
/* ── Drawer ───────────────────────────────────────────────────────────── */
.drawer {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.flags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.field small {
  font-size: 0.75rem;
}
.field input[type="text"],
.field input[type="date"],
.field select,
.field textarea {
  font-size: 0.92rem;
  font-family: inherit;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  min-width: 0;
}
.controls {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.control {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
}
.control .chip {
  flex: none;
  min-width: 3.6rem;
  justify-content: center;
  margin-top: 1px;
}
/* ── Configuration approval ───────────────────────────────────────────── */
.inventory__approval {
  white-space: nowrap;
}
@media (max-width: 640px) {
  .admin-governance__actions {
    width: 100%;
  }
  .admin-governance__actions .btn {
    flex: 1 1 auto;
    white-space: nowrap;
  }
}
.chip--warn {
  background: color-mix(in srgb, var(--color-warn) 16%, transparent);
  color: var(--color-warn-ink);
}
.chip--gap {
  background: color-mix(in srgb, var(--color-error) 12%, transparent);
  color: var(--color-error);
}
.chip--na {
  background: color-mix(in srgb, var(--color-ink) 5%, transparent);
  color: var(--color-muted);
  font-weight: 500;
}
.chip--neutral {
  background: color-mix(in srgb, var(--color-accent) 12%, transparent);
  color: var(--color-accent);
}
.chip--muted {
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
  color: var(--color-muted);
}
.ctrl-bar__seg--ok {
  background: var(--color-success);
}
.ctrl-bar__seg--warn {
  background: var(--color-warn);
}
.ctrl-bar__seg--gap {
  background: var(--color-error);
}
</style>
